import { useTranslation } from 'react-i18next';
import { FeedbackMessage } from '@sendible/design-system/src/components/molecules/ComposeDropDown';
import type { MediaType, PostType, composeTabs } from '@sendible/shared-state-bridge';
import { tabToNetworkMapping, tabToFriendlyNameMapping } from '@sendible/frontendv2/src/models/medias/consts';
import type { ValidationCodesType, ValidationDetailType } from './types';
import { formatValues } from './metadataFormatter';
import { useComposeBoxContext } from '../../composeBoxContext';

export const remapPosterValidationCode = (code: ValidationCodesType): ValidationCodesType => {
  return code === 'MaxSizeOutOfRange' ? 'MaxThumbnailFileSizeOutOfRange' : code;
};

export type PosterValidationErrorType = {
  platform: string;
  postType: string;
  level?: string;
  code?: string;
  allowed?: Record<string, unknown>;
  failureReason?: string;
};

const getPosterValidationCode = (code: string): ValidationCodesType => {
  return remapPosterValidationCode(code as ValidationCodesType);
};

const getPosterValidationsForNetworkAndPostType = (media: MediaType, networkName: string, postType: PostType): ValidationDetailType[] => {
  const rawPosterValidations =
    media.components?.poster?.validations &&
    media.components.poster.validations[networkName] &&
    media.components.poster.validations[networkName][postType];

  return rawPosterValidations?.map((validation) => ({ ...validation, code: getPosterValidationCode(validation.code) })) || [];
};

export const getValidationMessage = (
  t: (key: string, options?: Record<string, unknown>) => string,
  validation: ValidationDetailType
): FeedbackMessage => {
  const formatter = validation && formatValues[validation.code];
  const values = formatter && formatter(validation.allowed, t);

  const serialisedValues = values?.reduce(
    (acc, value, index) => {
      acc[`supported-${index + 1}`] = value;

      return acc;
    },
    {} as Record<string, string>
  );

  return {
    type: validation?.level === 'Required' ? 'danger' : 'info',
    message: t(`${validation?.code}-${validation?.level}`, serialisedValues || {}),
  };
};

export const getPosterValidationMessages = (
  media: MediaType,
  tabName: string,
  postType: PostType,
  t: (key: string, options?: Record<string, unknown>) => string
): FeedbackMessage[] => {
  return (
    getPosterValidationsForNetworkAndPostType(media, tabToNetworkMapping[tabName], postType)?.map((validation) => {
      return getValidationMessage(t, validation);
    }) || []
  );
};

export const useGetValidationForMedia = () => {
  const { t } = useTranslation('validations');
  const { profileListServices } = useComposeBoxContext();

  const getValidationsForNetwork = (media: MediaType, networkName: string, postType: PostType): ValidationDetailType[] => {
    const regularValidations = media.validations && media.validations[networkName] && media.validations[networkName][postType];
    const posterValidations = getPosterValidationsForNetworkAndPostType(media, networkName, postType);

    return [...(regularValidations || []), ...(posterValidations || [])];
  };

  const getValidationForMedia = (media: MediaType, tabId: composeTabs, postType: PostType): Array<FeedbackMessage> | undefined => {
    if (!postType || tabId === 'message') return;

    if (tabId === 'group') {
      const allMessages: FeedbackMessage[] = [];

      profileListServices.forEach((service) => {
        const networkName = tabToNetworkMapping[service];

        if (!networkName) return;

        const validations = getValidationsForNetwork(media, networkName, postType);

        validations.forEach((validation) => {
          const msg = getValidationMessage(t, validation);
          const serviceName = tabToFriendlyNameMapping[service] ?? service.charAt(0).toUpperCase() + service.slice(1);

          allMessages.push({
            ...msg,
            message: `<b>${serviceName} - </b> ${msg.message}`,
          });
        });
      });

      if (allMessages.length === 0) return;

      return allMessages.sort((a, b) => {
        const priority: Record<string, number> = { danger: 0, info: 1 };

        return (priority[a.type ?? ''] ?? 1) - (priority[b.type ?? ''] ?? 1);
      });
    }

    const networkName = tabToNetworkMapping[tabId];

    if (!networkName) return;

    // Get regular validations
    const regularValidations = media.validations && media.validations[networkName] && media.validations[networkName][postType];

    const posterValidations = getPosterValidationsForNetworkAndPostType(media, networkName, postType);

    const allValidations = [...(regularValidations || []), ...(posterValidations || [])];

    if (allValidations.length === 0) return;

    const orderedValidations = allValidations.sort((a, b) => {
      const priority = { Required: 0, Recommendation: 1 };

      return priority[a.level] - priority[b.level];
    });

    return orderedValidations.map((validation) => getValidationMessage(t, validation));
  };
  const getMainValidationType = (media: MediaType, tabId: composeTabs, postType: PostType) => {
    if (!postType || tabId === 'message') return undefined;

    if (tabId === 'group') {
      let hasRequired = false;
      let hasRecommendation = false;

      profileListServices.forEach((service) => {
        const networkName = tabToNetworkMapping[service];

        if (!networkName) return;

        const validations = getValidationsForNetwork(media, networkName, postType);

        if (validations.some((v: ValidationDetailType) => v.level === 'Required')) hasRequired = true;
        if (validations.some((v: ValidationDetailType) => v.level === 'Recommendation')) hasRecommendation = true;
      });

      if (hasRequired) return 'danger';
      if (hasRecommendation) return 'info';

      return undefined;
    }

    const networkName = tabToNetworkMapping[tabId];

    if (!networkName) return undefined;

    // Get regular validations
    const regularValidations = media.validations && media.validations[networkName] && media.validations[networkName][postType];

    // Get poster validations from components
    const posterValidations = getPosterValidationsForNetworkAndPostType(media, networkName, postType);

    // Merge both validation arrays
    const allValidations = [...(regularValidations || []), ...(posterValidations || [])];

    const hasRequired = allValidations?.some((validation: ValidationDetailType) => validation.level === 'Required');
    const hasRecommendation = allValidations?.some((validation: ValidationDetailType) => validation.level === 'Recommendation');

    if (hasRequired) return 'danger';
    if (hasRecommendation) return 'info';

    return undefined;
  };

  return { getValidationForMedia, getMainValidationType };
};
